import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';

import type { ChatUIMessage } from '@/entities/ai';
import { ENV } from '@/lib/config';
import { apiUrl } from '@/lib/api/api-url';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { i18n } from '@/shared/i18n';
import { withAgentUnavailableDetection } from '@/shared/lib/ai-stream';
import { useMountEffect } from '@/shared/hooks/useMountEffect';

import { parseGeneratedBrief } from '../../lib/parse-generated-brief';
import { BRIEF_WIDGET_KEY } from '../../model/brief-generation.constants';
import type { BriefGenerationStreamProps } from '../../model/useBriefGeneration';

const toError = (cause: unknown): Error =>
  cause instanceof Error ? cause : new Error(typeof cause === 'string' ? cause : String(cause));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getToolName = (data: unknown): string | null => {
  if (!isRecord(data) || !isRecord(data.data)) return null;
  return typeof data.data.toolName === 'string' ? data.data.toolName : null;
};

/**
 * The Relay host reports one `data-agent` node per agent invocation, reconciled by `nodeId` and
 * re-emitted as its status changes. The pipeline shows the sub-agents, so the workflow's own root
 * node is skipped: it runs for the whole generation and would light every step at once.
 */
const getAgentNode = (data: unknown): { agentId: string; status: string } | null => {
  if (!isRecord(data) || !isRecord(data.data)) return null;
  const { agentId, status, parentNodeId } = data.data;
  if (typeof agentId !== 'string' || typeof status !== 'string') return null;
  if (parentNodeId === null) return null;
  return { agentId, status };
};

/**
 * The workflow's output, off its own completed root node. The node is reconciled in place as its
 * status changes, so once the run ends this finds the completed one.
 */
const findWorkflowOutput = (parts: readonly unknown[], workflowId: string): unknown => {
  for (const part of parts) {
    if (!isRecord(part) || part.type !== 'data-agent' || !isRecord(part.data)) continue;
    const { agentId, parentNodeId, status, output } = part.data;
    if (parentNodeId === null && agentId === workflowId && status === 'completed') return output;
  }
  return undefined;
};

const getBriefWidgetData = (
  data: unknown,
): Parameters<BriefGenerationStreamProps['onWidgetCaptured']>[0] | null => {
  if (!isRecord(data) || !isRecord(data.data) || !isRecord(data.data.widget)) return null;
  if (data.data.widget.key !== BRIEF_WIDGET_KEY || !isRecord(data.data.widget.data)) return null;
  if (!('brief' in data.data.widget.data)) return null;
  return {
    brief: data.data.widget.data.brief,
  } as Parameters<BriefGenerationStreamProps['onWidgetCaptured']>[0];
};

export const BriefGenerationStream = ({
  contextId,
  agentId,
  workspaceId,
  payload,
  uploadedAssets,
  onStepEvent,
  onWidgetCaptured,
  onStreamError,
}: BriefGenerationStreamProps) => {
  /**
   * Which host serves this run. A context id means the reasoner in apps/api, which persists the
   * turn and pushes the brief out as a widget; without one the workflow workspace-api serves is
   * posted to directly and the brief comes back as the run's output.
   */
  const isWorkspaceApi = contextId === undefined;

  const { sendMessage, stop } = useChat<ChatUIMessage>({
    experimental_throttle: 100,
    transport: new DefaultChatTransport({
      api: isWorkspaceApi
        ? apiUrl(ENV.WORKSPACE_API_BASE_URL, `/api/v1/agents/${agentId}/stream`)
        : apiUrl(
            ENV.INFERENCE_ENGINE_API_BASE_URL,
            `/api/v1/reasoner/contexts/${contextId}/message`,
          ),
      credentials: 'include',
      fetch: withAgentUnavailableDetection(fetchWithAuth),
      prepareSendMessagesRequest: (opts) => {
        let existingHeaders: Record<string, string> = {};
        if (opts.headers instanceof Headers) {
          existingHeaders = Object.fromEntries(opts.headers.entries());
        } else if (opts.headers && typeof opts.headers === 'object') {
          existingHeaders = opts.headers as Record<string, string>;
        }
        return {
          api: opts.api,
          credentials: opts.credentials,
          body: opts.body ?? {},
          headers: {
            ...existingHeaders,
            'x-selected-workspace-id': workspaceId,
          },
        };
      },
    }),
    onData: (data) => {
      if (!isRecord(data) || typeof data.type !== 'string') return;
      const toolName = getToolName(data);
      // Widened off the chat protocol's own part union, which does not name Relay's node events:
      // this component reads a stream from either host and the two report progress differently.
      const eventType: string = data.type;
      switch (eventType) {
        // Neither runtime emits the other's events: the original wraps each step in a tracker
        // keyed by a localized label, while Relay reports a node per agent. Both are handled so
        // the modal advances whichever host served the run.
        case 'data-agent': {
          const node = getAgentNode(data);
          if (!node) break;
          if (node.status === 'running') onStepEvent('start', node.agentId);
          else if (node.status === 'completed') onStepEvent('complete', node.agentId);
          else if (node.status === 'error' || node.status === 'aborted')
            onStepEvent('error', node.agentId);
          break;
        }
        case 'data-toolStart':
          if (toolName) onStepEvent('start', toolName);
          break;
        case 'data-toolComplete':
          if (toolName) onStepEvent('complete', toolName);
          break;
        case 'data-toolError':
          if (toolName) onStepEvent('error', toolName);
          break;
        case 'data-widget': {
          if (!isRecord(data.data) || !isRecord(data.data.widget)) return;
          if (data.data.widget.key !== BRIEF_WIDGET_KEY) return;
          const widgetData = getBriefWidgetData(data);
          if (!widgetData) {
            onStreamError(
              new Error(
                i18n.t('briefs:briefGeneration.briefGenerationStream.malformedWidgetEvent'),
              ),
            );
            return;
          }
          onWidgetCaptured(widgetData);
          break;
        }
        default:
          // Other event types are not relevant to the brief generation pipeline.
          break;
      }
    },
    /**
     * The workspace-api path has no widget event to capture: the brief is the workflow's output,
     * carried on its own root node. Read from there rather than from the assistant text, which
     * holds the sub-agents' raw structured output and would be rejected, reporting a succeeded run
     * as a failure.
     *
     * Called from a `finally`, so an aborted or failed run lands here too. Those already reported
     * through `onError`, and reporting a parse failure on top would replace the real cause.
     */
    onFinish: ({ message, isAbort, isError, isDisconnect }) => {
      if (!isWorkspaceApi || isAbort || isError || isDisconnect) return;

      const brief = parseGeneratedBrief(findWorkflowOutput(message.parts, agentId));
      if (!brief) {
        onStreamError(
          new Error(i18n.t('briefs:briefGeneration.briefGenerationStream.malformedBriefOutput')),
        );
        return;
      }
      onWidgetCaptured({ brief });
    },
    onError: (cause) => {
      const error = toError(cause);
      // AbortError is the expected outcome of `cancel()` — don't surface it.
      if (error.name === 'AbortError') return;
      onStreamError(error);
    },
  });

  // Mount-time external system kickoff: fire the initial message so the BE
  // pipeline starts, and stop the stream on unmount. `useMountEffect` is the
  // sanctioned escape hatch — see `shared/hooks/useMountEffect`.
  useMountEffect(() => {
    let started = false;
    const startTimer = window.setTimeout(() => {
      started = true;
      const input: {
        templateId: string;
        title: string;
        instructions?: string;
        projectContext?: typeof payload.projectContext;
      } = {
        templateId: payload.templateId,
        title: payload.title,
      };
      if (payload.instructions.trim().length > 0) {
        input.instructions = payload.instructions;
      }
      if (payload.projectContext) {
        input.projectContext = payload.projectContext;
      }

      void sendMessage(
        {
          text: '',
          metadata: { isHidden: true },
          // The AI SDK accepts these custom keys on the message; runtime cast
          // keeps the call site readable without a top-level interface for what
          // is a one-shot, internal payload.
        } as Parameters<typeof sendMessage>[0],
        {
          body: isWorkspaceApi
            ? {
                // Inside `input` here, because the workflow declares both on its own schema. The
                // reasoner path reads them off the run context instead, which is why they sit
                // beside it there.
                input: {
                  ...input,
                  artifactIds: payload.artifactIds,
                  knowledgeBaseIds: payload.knowledgeBaseIds,
                },
                // References, not bytes: workspace-api resolves each id against assets-api.
                attachments: uploadedAssets.map((asset) => ({
                  mediaType: asset.mimeType,
                  // eslint-disable-next-line i18next/no-literal-string -- names the host resolver, not copy
                  kind: 'asset',
                  id: asset.assetId,
                  filename: asset.metadata.fileName,
                })),
              }
            : {
                input,
                files: uploadedAssets,
                artifactIds: payload.artifactIds,
                knowledgeBaseIds: payload.knowledgeBaseIds,
                agentId,
                metadata: { isHidden: true },
              },
        },
      );
    }, 0);

    return () => {
      window.clearTimeout(startTimer);
      if (started) void stop();
    };
  });

  return null;
};
